/* eslint-disable react/no-set-state */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import type * as Sentry from '@sentry/browser';

import { FragmentError } from './FragmentError';

interface SentryErrorBoundaryProps {
    readonly sentryClient?: Sentry.Scope;
    readonly dsn?: string;
    readonly children: ReactNode;
}

interface SentryErrorBoundaryState {
    hasError: boolean;
    eventId?: string;
}

class SentryErrorBoundary extends Component<SentryErrorBoundaryProps, SentryErrorBoundaryState> {
    public static getDerivedStateFromError() {
        return { hasError: true };
    }

    public constructor(properties: SentryErrorBoundaryProps) {
        super(properties);
        this.state = { hasError: false, eventId: undefined };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const eventId = this.props.sentryClient?.captureException(error, {
            captureContext: {
                extra: { componentStack: errorInfo.componentStack },
            },
        });

        this.setState({ eventId });
    }

    public render() {
        const { children, dsn, sentryClient } = this.props;
        const { eventId, hasError } = this.state;

        const sentryInitialized = !!sentryClient;
        if (sentryInitialized && hasError && eventId) {
            return <FragmentError dsn={dsn} eventId={eventId} />;
        }

        return children;
    }
}

export { SentryErrorBoundary };
